import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Form, FormField, FormTextArea, FormCheckbox } from 'components/form';
import { emailSchema, nameSchema, phoneSchema } from 'utils/validators';
import { Colors } from 'constants/ui';
import { GlobalScrollLock } from 'components/GlobalScrollLock';

import { requestProject } from '../../api';
import * as styled from '../styles/pitch-modal';

const URGENCIES = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'this_week', label: 'This week' },
  { value: 'asap', label: 'ASAP' },
] as const;

const schema = z.object({
  client_name: nameSchema,
  client_phone: phoneSchema,
  client_email: emailSchema,
  phone_is_whatsapp: z.boolean(),
  service_id: z.string().optional(),
  urgency: z.enum(['flexible', 'this_week', 'asap']),
  preferred_start: z.string().optional(),
  description: z
    .string()
    .trim()
    .min(1, 'Tell the provider what you need')
    .max(4000, 'Too long'),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  token: string;
  providerName: string;
  accentColor: string;
  services: { id: string; name: string; engagement_type?: string | null }[];
  onClose: () => void;
};

export const RequestProjectModal = ({
  token,
  providerName,
  accentColor,
  services,
  onClose,
}: Props) => {
  const projectServices = services.filter(
    (service) => service.engagement_type === 'project',
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as any,
    mode: 'onBlur',
    defaultValues: {
      client_name: '',
      client_phone: '',
      client_email: '',
      phone_is_whatsapp: false,
      service_id:
        projectServices.length === 1 ? projectServices[0].id : undefined,
      urgency: 'flexible',
      preferred_start: '',
      description: '',
    },
  });

  const [step, setStep] = useState<'form' | 'done'>('form');
  const [viewToken, setViewToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (values: FormValues) => {
    setError(null);
    return requestProject(token, {
      client_name: values.client_name,
      client_phone: values.client_phone,
      client_email: values.client_email,
      phone_is_whatsapp: values.phone_is_whatsapp,
      service_id: values.service_id || undefined,
      urgency: values.urgency,
      preferred_start: values.preferred_start || undefined,
      description: values.description.trim(),
    })
      .then((response) => {
        setViewToken(response.data.thread.view_token);
        setStep('done');
      })
      .catch(() => setError('Could not send your request. Please try again.'));
  };

  const threadUrl = viewToken
    ? `${window.location.origin}/messages/thread/${viewToken}`
    : null;

  return (
    <styled.Overlay onClick={onClose}>
      <GlobalScrollLock />
      <styled.SheetOuter onClick={(event) => event.stopPropagation()}>
        <styled.Sheet>
          {step === 'form' && (
            <Form form={form} onSubmit={handleSubmit}>
              <styled.TopRow>
                <styled.Title>Request a quote from {providerName}</styled.Title>
                <styled.CloseBtn type="button" onClick={onClose}>
                  ✕
                </styled.CloseBtn>
              </styled.TopRow>

              <styled.SectionLabel>Your contact</styled.SectionLabel>
              <FormField
                name="client_name"
                label="Your name *"
                placeholder="Your name"
              />
              <FormField
                name="client_phone"
                type="tel"
                label="Phone *"
                placeholder="+1 555 000 0000"
              />
              <FormCheckbox
                name="phone_is_whatsapp"
                label="This number is on WhatsApp"
              />
              <FormField
                name="client_email"
                type="email"
                label="Email *"
                placeholder="you@example.com"
              />

              <styled.SectionLabel>The job</styled.SectionLabel>

              {projectServices.length > 1 && (
                <div style={{ marginBottom: '1.6rem' }}>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '0.6rem',
                      fontSize: '1.3rem',
                      fontWeight: 600,
                    }}
                  >
                    Service
                  </label>
                  <select
                    {...form.register('service_id')}
                    style={{
                      width: '100%',
                      padding: '1rem 1.2rem',
                      borderRadius: '0.8rem',
                      border: `0.1rem solid ${Colors.border}`,
                      fontSize: '1.4rem',
                    }}
                  >
                    <option value="">General request</option>
                    {projectServices.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ marginBottom: '1.6rem' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.6rem',
                    fontSize: '1.3rem',
                    fontWeight: 600,
                  }}
                >
                  Urgency
                </label>
                <div
                  style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}
                >
                  {URGENCIES.map((urgencyOption) => (
                    <label
                      key={urgencyOption.value}
                      style={{
                        flex: 1,
                        padding: '0.8rem 1.2rem',
                        border: `0.1rem solid ${form.watch('urgency') === urgencyOption.value ? accentColor : Colors.border}`,
                        borderRadius: '0.8rem',
                        cursor: 'pointer',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '1.3rem',
                        color:
                          form.watch('urgency') === urgencyOption.value
                            ? accentColor
                            : Colors.slate,
                        background:
                          form.watch('urgency') === urgencyOption.value
                            ? `${accentColor}1A`
                            : 'transparent',
                      }}
                    >
                      <input
                        type="radio"
                        value={urgencyOption.value}
                        {...form.register('urgency')}
                        style={{ display: 'none' }}
                      />
                      {urgencyOption.label}
                    </label>
                  ))}
                </div>
              </div>

              <FormField
                name="preferred_start"
                type="date"
                label="Preferred start (optional)"
              />

              <FormTextArea
                name="description"
                label="Describe the job *"
                placeholder="What needs doing? Any context that helps us understand the scope."
                rows={5}
              />

              {error && <styled.ErrorText>{error}</styled.ErrorText>}

              <styled.PrimaryBtn
                type="submit"
                $accent={accentColor}
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? 'Sending…' : 'Send request'}
              </styled.PrimaryBtn>
            </Form>
          )}

          {step === 'done' && (
            <styled.SuccessBox>
              <styled.SuccessIcon $accent={accentColor}>✓</styled.SuccessIcon>
              <styled.SuccessTitle>Request sent</styled.SuccessTitle>
              <styled.SuccessText>
                {providerName} will review your request and get back to you by
                email. You'll hear back as soon as they've had a chance to look
                at the scope.
              </styled.SuccessText>
              {threadUrl && (
                <styled.LinkBox>
                  <styled.LinkLabel>
                    Or follow the conversation here:
                  </styled.LinkLabel>
                  <styled.LinkUrl href={threadUrl} $accent={accentColor}>
                    {threadUrl}
                  </styled.LinkUrl>
                </styled.LinkBox>
              )}
              <styled.PrimaryBtn $accent={accentColor} onClick={onClose}>
                Done
              </styled.PrimaryBtn>
            </styled.SuccessBox>
          )}
        </styled.Sheet>
      </styled.SheetOuter>
    </styled.Overlay>
  );
};
